// Type mapping utilities for converting TaskData to TaskInputRenderer-compatible format

import { TaskType } from '@/lib/sdk/chatbot/types';
import type { ValidationRule } from '@/lib/sdk/workflows/types';

/**
 * Maps TaskType enum to ui_component string for TaskInputRenderer compatibility
 *
 * TaskInputRenderer uses ui_component to determine which input component to render.
 * This mapping allows TaskData (which uses TaskType) to work with the same renderer.
 */
export function mapTaskTypeToUiComponent(type: TaskType | string): string {
  const mapping: Record<string, string> = {
    // Direct mappings to specialized inputs
    [TaskType.TEXT]: 'text_input',
    [TaskType.NUMBER]: 'number_input',
    [TaskType.DATE]: 'date_picker',
    [TaskType.CHOICE]: 'choice_selector',

    // Media types - render as text input (read-only for data editing)
    [TaskType.PHOTO]: 'text_input',
    [TaskType.VIDEO]: 'text_input',
    [TaskType.DOCUMENT]: 'text_input',
    [TaskType.MEDIA]: 'text_input',

    // Location types - render as text input
    [TaskType.LOCATION]: 'text_input',
    [TaskType.LOCATION_CHECK]: 'text_input',
    [TaskType.ADDRESS]: 'text_input',

    // Special types - render as text input
    [TaskType.REGULAR]: 'text_input',
    [TaskType.INVOICE]: 'text_input',
    [TaskType.VALET]: 'text_input',

    // System types (should not appear in edit)
    [TaskType.INTRO]: 'text_input',
    [TaskType.END]: 'text_input',
    [TaskType.TASKLIST]: 'text_input',
  };

  return mapping[type] || 'text_input';
}

/**
 * Builds a ValidationRule from TaskType
 *
 * For data editing, we make all fields optional since we're editing existing data.
 * The validation is mainly for input type hints.
 */
export function buildValidationFromType(type: TaskType | string): ValidationRule {
  // For data editing, fields are never required (editing existing values)
  const baseValidation: ValidationRule = {
    required: false,
  };

  switch (type) {
    case TaskType.NUMBER:
      return {
        ...baseValidation,
        type: 'number',
      };

    case TaskType.DATE:
      return {
        ...baseValidation,
        type: 'date',
      };

    case TaskType.TEXT:
    case TaskType.REGULAR:
      return {
        ...baseValidation,
        type: 'text',
      };

    case TaskType.CHOICE:
      return {
        ...baseValidation,
        type: 'choice',
      };

    case TaskType.PHOTO:
      return {
        ...baseValidation,
        type: 'photo',
        accept: ['image/*'],
      };

    case TaskType.VIDEO:
      return {
        ...baseValidation,
        type: 'video',
        accept: ['video/*'],
      };

    case TaskType.DOCUMENT:
      return {
        ...baseValidation,
        type: 'document',
        accept: ['application/pdf', 'application/msword', 'text/*'],
      };

    case TaskType.MEDIA:
      return {
        ...baseValidation,
        type: 'media',
        accept: ['image/*', 'video/*', 'application/pdf'],
      };

    default:
      return baseValidation;
  }
}

/**
 * Checks if a TaskType should be hidden from the edit UI
 * System types like intro/end/tasklist are not user-editable
 */
export function isHiddenTaskType(type: TaskType | string): boolean {
  const hiddenTypes = [TaskType.INTRO, TaskType.END, TaskType.TASKLIST];
  return hiddenTypes.includes(type as TaskType);
}

/**
 * Checks if a TaskType is a media type that shows existing attachments
 */
export function isMediaTaskType(type: TaskType | string): boolean {
  const mediaTypes = [TaskType.PHOTO, TaskType.VIDEO, TaskType.DOCUMENT, TaskType.MEDIA];
  return mediaTypes.includes(type as TaskType);
}
